/**
 * Meal Planner - Alpine.js Store and Components
 * Feature: 001-meal-planner-builder
 *
 * This module provides the interactive functionality for the weekly meal planner,
 * including state management, search, drag-and-drop, and cart integration.
 */

if (!window.Eurus.loadedScript.has("meal-planner.js")) {
  window.Eurus.loadedScript.add("meal-planner.js");

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      /**
       * Main Meal Planner Store
       * Manages global state for the weekly meal planner
       */
      Alpine.store("xMealPlanner", {
        // === State ===
        selectedFrequency: "1-week",
        selectedDay: "mon", // For mobile single-day view
        slots: {},
        weekDates: {},
        mealLibrary: [],
        collections: [],
        selectedCollectionId: null, // null means "all collections"
        searchQuery: "",
        searchResults: [],
        isSearchModalOpen: false,
        activeSlot: null,
        loading: false,
        errorMessage: null,
        clearConfirmOpen: false,
        draggedItem: null,
        isAnimating: false,
        _isRandomizing: false,
        _filledSlots: new Set(),
        // Animation style: 'fade' | 'slide-up' | 'slide-left' | 'perspective' | 'pop' | 'flip'
        animStyle: "fade",

        // === Computed Properties ===
        get totalMeals() {
          return Object.values(this.slots).filter(
            (item) => item !== null && item !== undefined,
          ).length;
        },
        get totalSnacks() {
          return Object.values(this.slots).filter(
            (item) =>
              item !== null &&
              item !== undefined &&
              ["Snacks", "Dessert"].includes(item.collectionTitle),
          ).length;
        },
        get totalPrice() {
          return Object.values(this.slots)
            .filter((item) => item !== null && item !== undefined)
            .reduce((sum, item) => sum + (item?.price || 0), 0);
        },
        get totalCalories() {
          const cals = {};
          ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].forEach((day) => {
            cals[day] =
              Object.keys(this.slots)
                .filter((_) => _.toLowerCase().indexOf(day) === 0)
                .filter((_) => !!this.slots[_])
                .reduce(
                  (sum, label) => sum + (this.slots[label]?.calories || 0),
                  0,
                ) || 0;
          });
          return cals;
        },
        get totalCompareAtPrice() {
          return Object.values(this.slots)
            .filter((item) => item !== null && item !== undefined)
            .reduce(
              (sum, item) => sum + (item?.compareAtPrice || item?.price || 0),
              0,
            );
        },

        get savings() {
          return this.totalCompareAtPrice - this.totalPrice;
        },

        get dietaryTags() {
          const tags = {};
          Object.values(this.slots)
            .filter((item) => item !== null && item !== undefined)
            .flatMap((item) => item?.dietaryTags || [])
            .forEach((tag) => {
              if (tag) {
                tags[tag] = (tags[tag] || 0) + 1;
              }
            });
          return tags;
        },

        get weekDateRange() {
          return "Monday - Sunday";
        },

        // === Collection Filtering ===
        get filteredMealLibrary() {
          if (!this.selectedCollectionId) {
            return this.mealLibrary;
          }
          return this.mealLibrary.filter(
            (item) => item.collectionId === this.selectedCollectionId,
          );
        },

        get selectedCollection() {
          if (!this.selectedCollectionId) return null;
          return this.collections.find(
            (c) => c.id === this.selectedCollectionId,
          );
        },

        // === Slot Actions ===
        setSlot(day, mealType, item) {
          const slotId = `${day}-${mealType}`;
          const wasEmpty = !this.slots[slotId];
          this.slots[slotId] = item;
          this.saveToSession();

          if (wasEmpty && item && !this._isRandomizing && !this._filledSlots.has(slotId)) {
            this._filledSlots.add(slotId);
            window.gtag && window.gtag("event","meal_planner_interaction");
          }
        },

        getSlot(day, mealType) {
          const slotId = `${day}-${mealType}`;
          return this.slots[slotId] || null;
        },

        clearSlot(day, mealType) {
          const slotId = `${day}-${mealType}`;
          this.slots[slotId] = null;
          this.saveToSession();
        },

        clearAllSlots() {
          this.slots = {};
          this.saveToSession();
        },

        // === Frequency Actions ===
        setFrequency(frequency) {
          this.selectedFrequency = frequency;
          this.saveToSession();
        },

        // === Day Selection Actions (Mobile) ===
        setSelectedDay(day) {
          this.selectedDay = day;
        },
        randomize() {
          const mealTypes = ["breakfast", "lunch", "dinner", "snack"];
          const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

          const allFilled = days.every((day) =>
            mealTypes.every((mt) => this.getSlot(day, mt)),
          );
          if (allFilled) {
            this.clearAllSlots();
          }

          this.isAnimating = true;
          this._isRandomizing = true;

          const getItemsByCollectionTitles = (titles) => {
            const titleArray = Array.isArray(titles) ? titles : [titles];
            return this.mealLibrary.filter(
              (item) =>
                item.collectionTitle &&
                titleArray.some((title) =>
                  item.collectionTitle
                    .toLowerCase()
                    .includes(title.toLowerCase()),
                ),
            );
          };

          const getRandomItem = (items) => {
            if (!items || items.length === 0) return null;
            const availableItems = items.filter(_=>_.variants.find(x=>x.available));
            return availableItems[Math.floor(Math.random() * availableItems.length)];
          };

          const breakfastItems = getItemsByCollectionTitles(["Breakfast"]);
          const lunchItems = getItemsByCollectionTitles([
            "Lunch",
            "Small Plates",
            "Salads",
            "Soups",
          ]);
          const dinnerItems = getItemsByCollectionTitles(["Entrées"]);
          const snackItems = getItemsByCollectionTitles(["Dessert", "Snacks"]);

          const usedDinnerItemIds = new Set();

          // Pre-populate usedDinnerItemIds from existing dinner slots so
          // auto-fill avoids duplicating them.
          days.forEach((day) => {
            const existing = this.getSlot(day, "dinner");
            if (existing) {
              usedDinnerItemIds.add(existing.variantId);
            }
          });

          const mealTypeItemMap = {
            breakfast: () => getRandomItem(breakfastItems),
            lunch: () => getRandomItem(lunchItems),
            dinner: () => {
              const available = dinnerItems.filter(
                (item) => !usedDinnerItemIds.has(item.variantId),
              );
              return getRandomItem(available);
            },
            snack: () => getRandomItem(snackItems),
          };

          days.forEach((day) => {
            mealTypes.forEach((mealType) => {
              if (this.getSlot(day, mealType)) return;

              const item = mealTypeItemMap[mealType]();
              if (!item) return;

              if (mealType === "dinner") {
                usedDinnerItemIds.add(item.variantId);
              }

              this.setSlot(day, mealType, item);
              Alpine.nextTick(() => {
                this.updateSlotVariant(
                  day,
                  mealType,
                  item.randomVariant().id,
                );
              });
            });
          });

          this.saveToSession();

          this._isRandomizing = false;

          setTimeout(() => {
            this.isAnimating = false;
          }, 1200);
        },
        goToNextDay() {
          const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
          const currentIndex = days.indexOf(this.selectedDay);
          if (currentIndex < days.length - 1) {
            this.selectedDay = days[currentIndex + 1];
          }
        },

        goToPrevDay() {
          const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
          const currentIndex = days.indexOf(this.selectedDay);
          if (currentIndex > 0) {
            this.selectedDay = days[currentIndex - 1];
          }
        },

        get nextDayInfo() {
          const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
          const fullDayNames = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          const currentIndex = days.indexOf(this.selectedDay);
          if (currentIndex < days.length - 1) {
            const nextDay = days[currentIndex + 1];
            return {
              day: nextDay,
              dayName: fullDayNames[currentIndex + 1],
            };
          }
          return null;
        },

        get currentDayFilledSlots() {
          const mealTypes = ["breakfast", "lunch", "dinner", "snack"];
          return mealTypes.filter(
            (mealType) => this.getSlot(this.selectedDay, mealType) !== null,
          ).length;
        },

        // === Session Persistence ===
        saveToSession() {
          try {
            const data = {
              selectedFrequency: this.selectedFrequency,
              selectedDay: this.selectedDay,
              slots: this.slots,
              savedAt: Date.now(),
            };
            sessionStorage.setItem(
              "epicured_meal_planner",
              JSON.stringify(data),
            );
          } catch (e) {
            console.warn("Failed to save meal planner to session:", e);
          }
        },

        loadFromSession() {
          try {
            const data = sessionStorage.getItem("epicured_meal_planner");
            if (data) {
              const parsed = JSON.parse(data);
              this.selectedFrequency = parsed.selectedFrequency || "1-week";
              this.selectedDay = parsed.selectedDay || "mon";
              this.slots = parsed.slots || {};
            }
          } catch (e) {
            console.warn("Failed to load meal planner from session:", e);
          }
        },

        clearSession() {
          try {
            sessionStorage.removeItem("epicured_meal_planner");
          } catch (e) {
            console.warn("Failed to clear meal planner session:", e);
          }
        },

        // === Cart Integration ===
        async addToCart() {
          const items = Object.entries(this.slots)
            .filter(([_, item]) => item !== null && item !== undefined)
            .map(([slotId, item]) => {
              const sellingPlanId = item.sellingPlans?.[this.selectedFrequency];
              return {
                id: item.variantId,
                quantity: 1,
                selling_plan: sellingPlanId,
                properties: {
                  _meal_slot: slotId,
                },
              };
            });

          if (items.length === 0) {
            this.errorMessage = "Please add at least one meal to your plan";
            return;
          }

          // Check for missing selling plans
          const missingPlans = items.filter((item) => !item.selling_plan);
          if (missingPlans.length > 0) {
            this.errorMessage =
              "Some items do not have the selected subscription frequency available";
            return;
          }

          this.loading = true;
          this.errorMessage = null;

          try {
            const sections = Alpine.store("xCartHelper")
              ? Alpine.store("xCartHelper")
                  .getSectionsToRender()
                  .map((s) => s.id)
              : ["cart-drawer", "cart-icon-bubble"];

            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/javascript",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: JSON.stringify({
                items,
                sections,
                sections_url: window.location.pathname,
              }),
            });

            const result = await response.json();

            if (response.status === 422 || result.status === 422) {
              throw new Error(
                result.description ||
                  result.message ||
                  "Failed to add items to cart",
              );
            }

            // Update cart UI
            if (Alpine.store("xCartHelper")) {
              Alpine.store("xCartHelper")
                .getSectionsToRender()
                .forEach((section) => {
                  const el = document.querySelector(section.selector);
                  if (el && result.sections?.[section.id]) {
                    el.innerHTML = getSectionInnerHTML(
                      result.sections[section.id],
                      section.selector,
                    );
                  }
                });

              // Update cart count
              const cartBubble = document.querySelector(
                "#cart-icon-bubble span",
              );
              if (cartBubble) {
                Alpine.store("xCartHelper").currentItemCount = parseInt(
                  cartBubble.innerHTML,
                );
              }
            }

            // Dispatch cart change event
            document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));

           window.gtag && window.gtag("event","meal_planner_cart_add");

            // Clear planner and redirect
            this.clearAllSlots();
            this.clearSession();
            window.location.href = "/cart";
          } catch (error) {
            console.error("Add to cart failed:", error);
            this.errorMessage =
              error.message || "Failed to add items to cart. Please try again.";

           window.gtag && window.gtag("event","meal_planner_cart_add");

            // Reload mini cart to ensure sync
            if (Alpine.store("xMiniCart")) {
              Alpine.store("xMiniCart").reLoad();
            }
          } finally {
            this.loading = false;
          }
        },

        // === Week Date Calculation ===
        calculateWeekDates() {
          const today = new Date();
          const dayOfWeek = today.getDay();

          // Calculate Monday (week start)
          const monday = new Date(today);
          const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          monday.setDate(today.getDate() + diff);

          const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
          this.weekDates = {};

          days.forEach((day, i) => {
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            this.weekDates[day] = {
              date: date.getDate(),
              month: date.toLocaleString("en-US", { month: "short" }),
              fullMonth: date.toLocaleString("en-US", { month: "long" }),
              year: date.getFullYear(),
              isToday: date.toDateString() === today.toDateString(),
              dayName: date.toLocaleString("en-US", { weekday: "short" }),
              fullDayName: date.toLocaleString("en-US", { weekday: "long" }),
            };
          });
        },

        // === Search Modal ===
        openSearchModal(day, mealType) {
          this.activeSlot = { day, mealType };
          this.isSearchModalOpen = true;
          this.searchQuery = "";
          this.searchResults = [];

        
            // Pre-select collection based on meal type
  const mealTypeCollectionMap = {
    breakfast: 'collection_X3AB3P',
    lunch:     'collection_MBTNkR',
    dinner:    'collection_QyemcL',
    snack:     'collection_AwxJXg',
  };

  const collectionId = mealTypeCollectionMap[mealType];
  if (collectionId) {
    this.selectedCollectionId = collectionId;
  } else {
    this.selectedCollectionId = null; // reset to "All" if no mapping
  }


          if (Alpine.store("xPopup")) {
            Alpine.store("xPopup").open = true;
          }
        //  setTimeout(()=>{
          Alpine.store("xMealPlanner").searchMeals("");
        //  }, 100);


         

        },

        closeSearchModal() {
          this.isSearchModalOpen = false;
          this.activeSlot = null;
          this.searchQuery = "";
          this.searchResults = [];

          if (Alpine.store("xPopup")) {
            Alpine.store("xPopup").close();
          }
        },

        // === Search ===
        searchMeals(query) {
          if (!query) {
            //return all in selectedCollection
            
            this.searchResults = this.filteredMealLibrary.slice(0, 50); // Limit to 20 results for performance
            return;
          }
          console.log("resultlength", this.searchResults.length);

          if (query.length < 2) {
            this.searchResults = [];
            return;
          }

          const lowerQuery = query.toLowerCase();
          const library = this.filteredMealLibrary;
          this.searchResults = library
            .filter((item) => {
              const titleMatch = item.title?.toLowerCase().includes(lowerQuery);
              const tagMatch = item.dietaryTags?.some((tag) =>
                tag?.toLowerCase().includes(lowerQuery),
              );
              return titleMatch || tagMatch;
            })
            .slice(0, 20); // Limit to 20 results for performance
        },

        selectSearchResult(item) {
          if (this.activeSlot) {
            this.setSlot(this.activeSlot.day, this.activeSlot.mealType, item);
          }
          this.closeSearchModal();
        },

        // === Drag and Drop ===
        setDraggedItem(item) {
          this.draggedItem = item;
        },

        clearDraggedItem() {
          this.draggedItem = null;
        },

        handleDrop(day, mealType) {
          if (this.draggedItem) {
            this.setSlot(day, mealType, this.draggedItem);
            this.clearDraggedItem();
          }
        },

        // === Meal Library ===
        loadMealLibrary(productsJson) {
          try {
            const products =
              typeof productsJson === "string"
                ? JSON.parse(productsJson)
                : productsJson;

            this.mealLibrary = products.map((p) => this.transformProduct(p));
          } catch (e) {
            console.error("Failed to load meal library:", e);
            this.mealLibrary = [];
          }
        },

        loadCollections(collectionsJson) {
          try {
            const collections =
              typeof collectionsJson === "string"
                ? JSON.parse(collectionsJson)
                : collectionsJson;

            this.collections = collections;
          } catch (e) {
            console.error("Failed to load collections:", e);
            this.collections = [];
          }
        },

        setCollectionFilter(collectionId) {
          this.selectedCollectionId = collectionId;
        },

        clearCollectionFilter() {
          this.selectedCollectionId = null;
        },

        /**
         * Transform a product from Liquid to the meal planner format.
         * Includes all variants data with the first variant selected by default.
         * Each variant has: id, title, price, compareAtPrice, calories, available, sellingPlans
         */
        transformProduct(product) {
          // Process all variants
          const variants = (product.variants || []).map((variant) => {
            const sellingPlans = this.extractSellingPlans(variant);
            let calories = null;

            if (variant.nutrition && variant.nutrition.length) {
              const calorieLabel = variant.nutrition.find((_) =>
                _.startsWith("Calories"),
              );
              if (calorieLabel) {
                const splitCals = calorieLabel.split(":");
                if (splitCals.length > 1) {
                  calories = Number(splitCals[1].trim());
                  if (isNaN(calories)) calories = null;
                }
              }
            }

            return {
              id: variant.id,
              title: variant.title || "Default",
              price: variant.selling_plan_allocations[0]?.price || variant.price || 0, //this needs to be.. selling plan price.. 
              compareAtPrice:  variant.price || null,                        
              calories,
              available: variant.available !== false,
              sellingPlans,
            };
          });

          // Default to first variant
          const defaultVariant = variants[0] || {};

          return {
            productId: product.id,
            variantId: defaultVariant.id,
            selectedVariantId: defaultVariant.id,
            title: product.title,
            handle: product.handle,
            collectionId: product.collection_id || null,
            collectionTitle: product.collection_title || null,
            image: product.featured_image || product.images?.[0] || "",
            images: product.images || [],
            price: defaultVariant.price || 0,
            compareAtPrice: defaultVariant.compareAtPrice || null,            
            calories: defaultVariant.calories || null,
            dietaryTags:
              product.metafields?.custom?.dietary_tags ||
              product.metafields?.dietary_tags ||
              product.tags?.filter((t) =>
                [
                  "Low FODMAP",
                  "Vegan",
                  "GF",
                  "Gluten-Free",
                  "Vegetarian",
                  "Dairy-Free",
                ].includes(t),
              ) ||
              [],
            sellingPlans: defaultVariant.sellingPlans || {},
            available: defaultVariant.available !== false,
            variants,
            hasMultipleVariants: variants.length > 1,
            variantTitle: defaultVariant.title || "",
            randomVariant() {
              if(variants.length === 1) return this.variants[0];
              const instock = this.variants.filter(_=>_.available);
              if(!instock.length) return this.variants[0]; //address higher up
              return instock[Math.floor(Math.random() * instock.length)];              
            },
          };
        },

        /**
         * Update the selected variant for a product in a slot.
         * Updates price, calories, availability, and selling plans based on the selected variant.
         */
        updateSlotVariant(day, mealType, variantId) {
          const slotId = `${day}-${mealType}`;
          const item = this.slots[slotId];
          if (!item || !item.variants) return;

          // Convert variantId to string for comparison (Liquid outputs strings)
          const targetVariantId = String(variantId);
          const variant = item.variants.find(
            (v) => String(v.id) === targetVariantId,
          );
          if (!variant) return;

          // Replace the entire slots object to trigger Alpine reactivity
          this.slots = {
            ...this.slots,
            [slotId]: {
              ...item,
              variantId: variant.id,
              selectedVariantId: variant.id,
              price: variant.price,
              compareAtPrice: variant.compareAtPrice,
              calories: variant.calories,
              available: variant.available,
              sellingPlans: variant.sellingPlans,
              variantTitle: variant.title,
            },
          };

          this.saveToSession();
        },

        extractSellingPlans(variant) {
          const plans = {};

          if (variant?.selling_plan_allocations) {
            variant.selling_plan_allocations.forEach((spa) => {
          
           //   variant.price = spa.price;
              const name = spa.selling_plan?.name?.toLowerCase() || "";
              const id = spa.selling_plan?.id;

              if (name.includes("1 week") || name.includes("weekly")) {
                plans["1-week"] = id;
              } else if (
                name.includes("2 week") ||
                name.includes("bi-weekly")
              ) {
                plans["2-week"] = id;
              } else if (name.includes("4 week") || name.includes("monthly")) {
                plans["4-week"] = id;
              }
            });
          }

          return plans;
        },

        findProductByVariantId(variantId) {
          const targetId = String(variantId);
          return this.mealLibrary.find((item) =>
            item.variants?.some((v) => String(v.id) === targetId),
          );
        },

        /**
         *  ?meals=<28 variants, comma separated, leave blanks>
         * example:?meals=1,1,,,,,1,,,1,,1,,1,1,,,,,,1,,,,,1,1,1
         * @returns void
         */
        seedFromUrl() {
          const params = new URLSearchParams(window.location.search);
          const mealsParam = params.get("meals");
          if (!mealsParam) return;

          const values = mealsParam.split(",");
          if (values.length !== 28) return;

          const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
          const mealTypes = ["breakfast", "lunch", "dinner", "snack"];

          values.forEach((val, i) => {
            const trimmed = val.trim();
            if (!trimmed || trimmed === "null" || trimmed === "undefined") return;

            const day = days[Math.floor(i / 4)];
            const mealType = mealTypes[i % 4];
            const product = this.findProductByVariantId(trimmed);

            if (product) {
              this.setSlot(day, mealType, product);
              const variant = product.variants.find(
                (v) => String(v.id) === String(trimmed),
              );
              if (variant && String(variant.id) !== String(product.selectedVariantId)) {
                this.updateSlotVariant(day, mealType, variant.id);
              }
            }
          });

          this.saveToSession();
        },

        // === Initialization ===
        init() {
          this.calculateWeekDates();
          this.loadFromSession();
        },
      });

      /**
       * Meal Planner Grid Component
       * Handles individual slot interactions
       * For mobile view, pass 'selectedDay' as day to make it reactive
       */
      Alpine.data("xMealPlannerSlot", (day, mealType, mealLabel) => ({
        day,
        isSelectedDayRef: day === "selectedDay",
        mealType,
        mealLabel,
        isDraggingOver: false,

        get store() {
          return this.$store.xMealPlanner;
        },

        init() {
          if (this.isSelectedDayRef) {
            this.day = this.store.selectedDay;
            this.$watch("$store.xMealPlanner.selectedDay", (value) => {
              this.day = value;
            });
          }
        },

        get slotId() {
          return `${this.day}-${this.mealType}`;
        },

        get item() {
          return this.store.getSlot(this.day, this.mealType);
        },

        get hasItem() {
          return this.item !== null && this.item !== undefined;
        },

        openModal(m) {        
        
          this.store.openSearchModal(this.day, this.mealType);
         
        },

        removeItem() {
          this.store.clearSlot(this.day, this.mealType);
        },

        handleDrop(e) {
          e.preventDefault();
          this.isDraggingOver = false;
          this.store.handleDrop(this.day, this.mealType);
        },
      }));

      /**
       * Meal Planner Item Component
       * Handles draggable meal items in sidebar
       */
      Alpine.data("xMealPlannerItem", (item) => ({
        item,
        isDragging: false,

        handleDragStart(e) {
          this.isDragging = true;
          Alpine.store("xMealPlanner").setDraggedItem(item);
          e.dataTransfer.effectAllowed = "copy";
          e.dataTransfer.setData("text/plain", item.variantId);
        },

        handleDragEnd(e) {
          this.isDragging = false;
          Alpine.store("xMealPlanner").clearDraggedItem();
        },

        selectItem() {
          const store = Alpine.store("xMealPlanner");
          if (store.activeSlot) {
            store.selectSearchResult(item);
          }
        },
      }));

      /**
       * Meal Planner Search Component
       * Handles search input with debouncing
       */
      Alpine.data("xMealPlannerSearch", () => ({
        query: "",
        debounceTimer: null,
       
        handleInput(e) {
          this.query = e.target.value;

          // Debounce search
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => {
            Alpine.store("xMealPlanner").searchMeals(this.query);
          }, 300);
        },

        clearSearch() {
          this.query = "";
          Alpine.store("xMealPlanner").searchResults = [];
        },
      }));

      /**
       * Meal Planner Footer Component
       * Handles totals and checkout
       */
      Alpine.data("xMealPlannerFooter", (moneyFormat) => ({
        moneyFormat: moneyFormat || "${{amount}}",

        get store() {
          return Alpine.store("xMealPlanner");
        },

        get canCheckout() {
          return this.store.totalMeals > 0 && !this.store.loading;
        },

        formatPrice(cents) {
          if (!cents) return "";
          if (Alpine.store("xHelper")?.formatMoney) {
            return Alpine.store("xHelper").formatMoney(cents, this.moneyFormat);
          }
          return `$${(cents / 100).toFixed(2)}`;
        },

        handleCheckout() {
          if (this.canCheckout) {
            this.store.addToCart();
          }
        },

        handleClearWeek() {
          this.store.clearConfirmOpen = true;
        },

        confirmClear() {
          this.store.clearAllSlots();
          this.store.clearConfirmOpen = false;
        },

        cancelClear() {
          this.store.clearConfirmOpen = false;
        },
      }));

      /**
       * Meal Planner Modal Component
       * Handles modal accessibility and interactions
       */
      Alpine.data("xMealPlannerModal", () => ({
        get store() {
          return Alpine.store("xMealPlanner");
        },

        get isOpen() {
          return this.store.isSearchModalOpen;
        },

        closeModal() {
          this.store.closeSearchModal();
        },

        handleKeydown(e) {
          if (e.key === "Escape") {
            this.closeModal();
          }
        },
      }));
    });
  });
}
